import React, { useMemo } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  LinearProgress,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WarningIcon from "@mui/icons-material/Warning";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ScienceIcon from "@mui/icons-material/Science";
import BusinessIcon from "@mui/icons-material/Business";
import SpeedIcon from "@mui/icons-material/Speed";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonIcon from "@mui/icons-material/Person";
import "./Panels.css";

export default function EnhancedAnalyticsPanel({ data }) {
  const analytics = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        totalSamples: 0,
        uniqueOrders: 0,
        uniqueClients: 0,
        overdueAnalysis: 0,
        overdueReports: 0,
        dueTodayAnalysis: 0,
        dueTodayReports: 0,
        priorityStats: {},
        topParameters: [],
        topClients: [],
        topAnalysts: [],
        tatStats: {},
        avgDaysInHouse: 0
      };
    }

    const currentDate = new Date();
    
    // Basic counts
    const totalSamples = data.length;
    const uniqueOrders = new Set(data.map(row => row.orderNumber).filter(Boolean)).size;
    const uniqueClients = new Set(data.map(row => row.clientName).filter(Boolean)).size;
    
    // Due date analysis
    const overdueAnalysis = data.filter(row => 
      row.dueForAnalyst && new Date(row.dueForAnalyst) < currentDate
    ).length;
    
    const overdueReports = data.filter(row => 
      row.reportDueDate && new Date(row.reportDueDate) < currentDate
    ).length;
    
    const dueTodayAnalysis = data.filter(row => {
      if (!row.dueForAnalyst) return false;
      const dueDate = new Date(row.dueForAnalyst);
      return dueDate.toDateString() === currentDate.toDateString();
    }).length;
    
    const dueTodayReports = data.filter(row => {
      if (!row.reportDueDate) return false;
      const dueDate = new Date(row.reportDueDate);
      return dueDate.toDateString() === currentDate.toDateString();
    }).length;
    
    // Priority distribution
    const priorityStats = data.reduce((acc, row) => {
      const priority = (row.priority || "Regular").toLowerCase();
      if (priority.includes("rush") || priority.includes("urgent") || priority.includes("high")) {
        acc.rush = (acc.rush || 0) + 1;
      } else if (priority.includes("medium")) {
        acc.medium = (acc.medium || 0) + 1;
      } else {
        acc.regular = (acc.regular || 0) + 1;
      }
      return acc;
    }, {});
    
    // Parameter analysis
    const parameterStats = data.reduce((acc, row) => {
      if (row.parameter) {
        acc[row.parameter] = (acc[row.parameter] || 0) + 1;
      }
      return acc;
    }, {});
    
    const topParameters = Object.entries(parameterStats)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);
    
    // Client analysis
    const clientStats = data.reduce((acc, row) => {
      if (row.clientName) {
        acc[row.clientName] = (acc[row.clientName] || 0) + 1;
      }
      return acc;
    }, {});
    
    const topClients = Object.entries(clientStats)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);
    
    // Analyst analysis
    const analystStats = data.reduce((acc, row) => {
      if (row.analyst) {
        acc[row.analyst] = (acc[row.analyst] || 0) + 1;
      }
      return acc;
    }, {});
    
    const topAnalysts = Object.entries(analystStats)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);
    
    // TAT analysis based on turnAroundTime
    const tatStats = data.reduce((acc, row) => {
      if (row.turnAroundTime) {
        const tat = parseInt(row.turnAroundTime);
        if (!isNaN(tat)) {
          if (tat <= 5) acc.express = (acc.express || 0) + 1;
          else if (tat <= 10) acc.standard = (acc.standard || 0) + 1;
          else acc.extended = (acc.extended || 0) + 1;
        }
      }
      return acc;
    }, {});
    
    // Average days in house
    const daysInHouseValues = data
      .map(row => row.daysInHouse)
      .filter(days => days != null && !isNaN(days));
    
    const avgDaysInHouse = daysInHouseValues.length > 0
      ? Math.round(daysInHouseValues.reduce((sum, days) => sum + days, 0) / daysInHouseValues.length * 10) / 10
      : 0;
    
    return {
      totalSamples,
      uniqueOrders,
      uniqueClients,
      overdueAnalysis,
      overdueReports,
      dueTodayAnalysis,
      dueTodayReports,
      priorityStats,
      topParameters,
      topClients,
      topAnalysts,
      tatStats,
      avgDaysInHouse
    };
  }, [data]);

  const StatCard = ({ title, value, icon, color = "primary", subtitle = null }) => (
    <Card className="enhanced-status-tile" sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            {icon}
            <Typography variant="h6" component="div">
              {value}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const PriorityBar = ({ label, count, total, color }) => {
    const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
      <Box mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2">{label}</Typography>
          <Chip label={count} size="small" />
        </Box>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 8,
            borderRadius: 4,
            "& .MuiLinearProgress-bar": {
              background: color,
              borderRadius: 4,
            },
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {percentage}% of total
        </Typography>
      </Box>
    );
  };

  const TopListItem = ({ label, count, rank }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
      <Box display="flex" alignItems="center" gap={1}>
        <Chip label={rank} size="small" variant="outlined" />
        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
          {label.length > 30 ? `${label.substring(0, 30)}...` : label}
        </Typography>
      </Box>
      <Typography variant="body2" color="primary" fontWeight="bold">
        {count}
      </Typography>
    </Box>
  );

  return (
    <Box className="modern-panel">
      
      {/* Key Metrics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Samples"
            value={analytics.totalSamples}
            icon={<ScienceIcon color="primary" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Unique Orders"
            value={analytics.uniqueOrders}
            icon={<AssignmentIcon color="secondary" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Clients"
            value={analytics.uniqueClients}
            icon={<BusinessIcon color="info" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Days in House"
            value={analytics.avgDaysInHouse}
            icon={<AccessTimeIcon color="warning" />}
            subtitle="days"
          />
        </Grid>
      </Grid>

      {/* Urgent Items */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Overdue Analysis"
            value={analytics.overdueAnalysis}
            icon={<WarningIcon color="error" />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Overdue Reports"
            value={analytics.overdueReports}
            icon={<WarningIcon color="error" />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Due Today (Analysis)"
            value={analytics.dueTodayAnalysis}
            icon={<AccessTimeIcon color="warning" />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Due Today (Reports)"
            value={analytics.dueTodayReports}
            icon={<AccessTimeIcon color="warning" />}
            color="warning"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Priority Distribution */}
        <Grid item xs={12} md={4}>
          <Paper className="enhanced-status-tile" sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Priority Distribution
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {analytics.priorityStats.rush > 0 && (
              <PriorityBar
                label="Rush/High Priority"
                count={analytics.priorityStats.rush || 0}
                total={analytics.totalSamples}
                color="linear-gradient(135deg, #f44336 0%, #ff7961 100%)"
              />
            )}
            
            {analytics.priorityStats.medium > 0 && (
              <PriorityBar
                label="Medium Priority"
                count={analytics.priorityStats.medium || 0}
                total={analytics.totalSamples}
                color="linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)"
              />
            )}
            
            <PriorityBar
              label="Regular Priority"
              count={analytics.priorityStats.regular || 0}
              total={analytics.totalSamples}
              color="linear-gradient(135deg, #4caf50 0%, #81c784 100%)"
            />
          </Paper>
        </Grid>

        {/* TAT Distribution */}
        <Grid item xs={12} md={4}>
          <Paper className="enhanced-status-tile" sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Turnaround Time Distribution
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {analytics.tatStats.express > 0 && (
              <PriorityBar
                label="Express (≤5 days)"
                count={analytics.tatStats.express || 0}
                total={analytics.totalSamples}
                color="linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)"
              />
            )}
            
            {analytics.tatStats.standard > 0 && (
              <PriorityBar
                label="Standard (6-10 days)"
                count={analytics.tatStats.standard || 0}
                total={analytics.totalSamples}
                color="linear-gradient(135deg, #4caf50 0%, #81c784 100%)"
              />
            )}
            
            {analytics.tatStats.extended > 0 && (
              <PriorityBar
                label="Extended (>10 days)"
                count={analytics.tatStats.extended || 0}
                total={analytics.totalSamples}
                color="linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)"
              />
            )}
          </Paper>
        </Grid>

        {/* Top Parameters */}
        <Grid item xs={12} md={4}>
          <Paper className="enhanced-status-tile" sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Top Parameters
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {analytics.topParameters.map(([parameter, count], index) => (
              <TopListItem
                key={parameter}
                label={parameter}
                count={count}
                rank={index + 1}
              />
            ))}
          </Paper>
        </Grid>

        {/* Top Clients */}
        <Grid item xs={12} md={6}>
          <Paper className="enhanced-status-tile" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Clients by Sample Volume
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {analytics.topClients.map(([client, count], index) => (
              <TopListItem
                key={client}
                label={client}
                count={count}
                rank={index + 1}
              />
            ))}
          </Paper>
        </Grid>

        {/* Top Analysts */}
        <Grid item xs={12} md={6}>
          <Paper className="enhanced-status-tile" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Analyst Workload
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {analytics.topAnalysts.length > 0 ? (
              analytics.topAnalysts.map(([analyst, count], index) => (
                <TopListItem
                  key={analyst}
                  label={analyst}
                  count={count}
                  rank={index + 1}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No analyst assignments found
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}